import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import Tab from '@lwt-helix/tab';
import { Row } from '@lwt-helix/layout';
import StickyFooter from '@lwt-helix/sticky-footer';
import { agentProductionTerms, TimePeriods, modules } from '../../../constants';
import AgentContactInfo from '../agentSlideout/AgentContactInfo';
import TransactionCoverageTab from './tabs/TransactionCoverageTab';
import Inventory from './tabs/inventory/Inventory';
import { useQuery } from '../../../common/hooks/location';
import Production from './tabs/production/Production';
import TableChartToggle, { tableChartModes } from '../common/TableChartToggle';
import { setStateData } from '../../../common/helpers/state';
import TimePeriodSelector from './tabs/TimePeriodSelector';
import { mapIdToTableData, monthAndYearFormat } from '../../helpers/dataFormatters';
import {
    agentProfileTabs as tabIndex,
    productionOptions,
    transactionCoverageOptions,
    agentProfileTabs,
    agentProfileTabNamesByIndex
} from '../../constants/agentProductionConstants';
import { maskNullDate } from './tabs/helpers/exportHelpers';
import { getExportButtonProps as getProductionExportButtonProps } from './tabs/helpers/productionExportHelpers';
import { getExportButtonProps as getInventoryExportButtonProps } from './tabs/helpers/inventoryExportHelpers';
import { getExportButtonProps as getTransactionCoverageExportButtonProps } from './tabs/helpers/transactionCoverageExportHelpers';
import { chartModes } from '../../helpers/agentDataCalculators';
import { Routes } from '../../../common/routes/routes';
import { getTimePeriodData, setTimePeriodData } from '../../helpers/browserStorageHelper';
import OfficeHistoryTab from './tabs/officeHistory/OfficeHistoryTab';
import { useAgentProfileStore, useCommonStore, useSearchStore } from '../../../store/store';
import { getExportButton } from '../../../common/helpers/export';
import { usePDFStore } from '../../../store/pdf/store';
import { exportTypes } from '../helpers/agentProfile';
import { exportButtonTypes } from '../../../common/constants/export';
import { DownloadResultsModal } from '../../../common/components/DownloadResultsModal';
import PropTypes from 'prop-types';
import { isAgentMetrics } from '../../../constants/app';
import { getAmSearchCriteria } from '../../helpers/agentProductionHelpers';
import { getIsInternalEmailAddress } from '../../../common/helpers/featureToggles';
import XamAgentSelector from '../../../agent-metrics/components/XamAgentSelector';

const ChartTabWrapper = ({ children }) => (
    <div style={{ width: '100%' }}>
        {children}
    </div>
);

const AgentProfile = ({ agentData, module, mlsProps, tokenInfo }) => {
    const isXamInternalUser = isAgentMetrics && getIsInternalEmailAddress(tokenInfo?.email);
    const isAgMovement = module === modules.proficiencyMetrics.agentMovement;
    const agentProfileData = isAgMovement ? agentData?.agMovAgentBreakdownSearch : agentData;
    const query = useQuery();
    const agentId = query.get('id');
    const chosenIndex = parseInt(query.get('tabIndex'));
    const graphModeParam = query.get('graph');
    const chartModeParam = query.get('chartMode');
    const queryIntervalType = query.get('timeInterval');
    const queryIsShowBarLabels = query.get('isShowBarLabels') !== 'false';
    const history = useHistory();

    const {
        setSelectedAgentId
    } = useAgentProfileStore(state => ({
        setSelectedAgentId: state.setSelectedAgentId
    }));

    const {
        membership,
        setAgentDetailNavigation,
        agentMetricsAgentId,
        agentMetricsMlsId,
    } = useCommonStore(state => {
        const mlsAgent = state?.agentInfo?.[0]?.mlsAgentId;
        return({
            membership: state.membership,
            setAgentDetailNavigation: state.setAgentDetailNavigation,
            agentMetricsAgentId: mlsAgent?.agentId,
            agentMetricsMlsId: mlsAgent?.mlsId,
        });
    });

    const {
        search,
        loadedSearchCriteria
    } = useSearchStore(state => ({
        search: state[module]?.search,
        loadedSearchCriteria: state[module]?.loadedSearchCriteria
    }));

    const {
            timeIntervals,
        } = useCommonStore(state => ({
            timeIntervals: state.timeIntervals,
        }));

    const {
        setAgentProfileExportDetails,
        setAgentProfileTabIndex,
        setAgentProfileTimePeriod,
    } = usePDFStore(state => ({
        setAgentProfileExportDetails: state.setAgentProfileExportDetails,
        setAgentProfileTabIndex: state.setAgentProfileTabIndex,
        setAgentProfileTimePeriod: state.setAgentProfileTimePeriod,
    }));

    const [state, setState] = useState({
        agent: null,
        agentsData: null,
        mlsId: agentMetricsMlsId ?? null,
        agentId: agentMetricsAgentId ?? null,
        productionTimePeriod: getTimePeriodData() || TimePeriods['Last 24 Months'],
        productionMode: chartModeParam || tableChartModes.CHART,
        productionGraphMode: graphModeParam || chartModes.VOLUME,
        productionIsLabelEnabled: queryIsShowBarLabels,
        transactionTimePeriod: TimePeriods['Last 12 Months'],
        transactionCoverageMode: tableChartModes.CHART,
        currentTabIndex: chosenIndex ? chosenIndex : tabIndex.PRODUCTION,
        isShowTimePeriodSelector: chosenIndex !== tabIndex.INVENTORY,
        currentListingMonth: null,
        isTimePeriodSelectorDisabled: false,
        searchCriteria: null,
        mlsName: null,
        exportProps: {
            headers: null,
            data: null,
            filename: null,
            isVisible: false
        },
        shouldStickyFooterHide: false
    });

    useEffect(() => {
        setSelectedAgentId(agentId);
    }, [agentId]);

    const xamAgentSelectorProps = {
        setState,
        setSelectedAgentId,
        agentMetricsAgentId,
        agentIdQuery: agentId
    };

    const setShouldStickyFooterHide = (shouldHide) => setStateData('shouldStickyFooterHide', shouldHide, setState);

    const processFromProduction = (fromProduction) => {
        const paramsGetExportButtonProps = {
            data: sortExportData(fromProduction.data),
            dates: fromProduction.currentIntervalDates,
            tableChartMode: fromProduction.tableChartMode,
            agent: state.agent,
            gridRef: fromProduction.gridRef,
            mlsName: state.mlsName
        };

        const exportProps = getProductionExportButtonProps(paramsGetExportButtonProps);
        return setState((prevState) => {
            return {
                ...prevState,
                exportProps: {...exportProps, gridRef: paramsGetExportButtonProps.gridRef}
            };
        });
    };

    const sortExportData = (exportData) => {
        return [...exportData].sort((a, b) => new Date(b.month) - new Date(a.month));
    };

    const processFromInventory = (fromInventory) => {
        const { underContractRawData, forSaleRawData, shouldShowExportButton, gridRef } = fromInventory;

        const data = [];

        const transformForExport = (d) => {
            const item = {
                ...d,
                dateList: maskNullDate(d.dateList),
                dateCont: maskNullDate(d.dateCont)
            };
            data.push(item);
        };

        forSaleRawData?.forEach(transformForExport);
        underContractRawData?.forEach(transformForExport);

        const isShowExportButton = shouldShowExportButton && !!data?.length;
        const paramsGetExportButtonProps = {
            data,
            agent: state.agent,
            mlsName: state.mlsName,
            isShowExportButton
        };
        const exportProps = getInventoryExportButtonProps(paramsGetExportButtonProps);

        setState((prevState) => {
            return {
                ...prevState,
                exportProps: {...exportProps, gridRef}
            };
        });
    };

    const processFromTransactionCoverage = (fromTransactionCoverage) => {
        const paramsGetExportButtonProps = {
            data: fromTransactionCoverage.data,
            area: fromTransactionCoverage.area,
            statusType: fromTransactionCoverage.statusType,
            tableChartMode: state.transactionCoverageMode,
            agent: state.agent,
            gridRef: fromTransactionCoverage.gridRef,
            mlsName: state.mlsName
        };

        const exportProps = getTransactionCoverageExportButtonProps(paramsGetExportButtonProps);
        setState((prevState) => {
            return {
                ...prevState,
                exportProps: {...exportProps, gridRef: paramsGetExportButtonProps.gridRef}
            };
        });
    };

    const processFromOfficeHistory = () => {
        const exportProps = {
            isVisible: false
        };
        setState((prevState) => {
            return {
                ...prevState,
                exportProps
            };
        });
    };

    const mlsProviders = useRef([]);

    const shouldRender = !!module;

    useEffect(() => {
        if (!shouldRender) {
            history.push('/');
        }
    }, []);

    useEffect(() => {
        mlsProviders.current = membership;
    }, [membership]);

    useEffect(() => {
        if (!shouldRender) return;
        const mlsId = isAgentMetrics ? agentMetricsMlsId : search?.mlsId;
        if (mlsId && Array.isArray(mlsProviders.current) && mlsProviders.current.length) {
            const mlsName = mlsProviders.current.find(
                (p) => p?.mlsId === mlsId
            )?.shortDescription;
            setStateData('mlsName', mlsName, setState);
        }
    }, [mlsProviders.current, search, agentMetricsMlsId]);

    useEffect(() => {
        if (!loadedSearchCriteria?.realEstateDatasourceIdsWithFilters?.length && !isAgentMetrics) return;
        const searchCriteria = loadedSearchCriteria ?? getAmSearchCriteria(state.mlsId);
        const mlsId =
            searchCriteria.realEstateDatasourceIdsWithFilters[0]
                .realEstateDatasourceId;
        const intervalType = queryIntervalType || searchCriteria.timePeriod.intervalType;
        const productionTimePeriod = getTimePeriodData() || TimePeriods['Last 24 Months'];
        const transactionTimePeriod =
            (transactionCoverageOptions.some((o) => o.value === intervalType) &&
                intervalType) ||
            TimePeriods['Last 12 Months'];

        setState((prevState) => ({
            ...prevState,
            searchCriteria,
            mlsId,
            productionTimePeriod,
            transactionTimePeriod,
            agentId: agentId ?? state.agentId,
        }));
    }, [loadedSearchCriteria]);

    useEffect(() => {
        const revisedAgentData = mapIdToTableData(agentProfileData);
        setStateData('agentsData', revisedAgentData || [], setState);
    }, [agentProfileData]);

    useEffect(() => {
        if (!shouldRender) return;
        const idParam = new URL(window.location.href).searchParams.get('id') ?? state.agentId;
        if (state.agentsData != null) {
            const agentFound = state.agentsData.find((agent) => agent.agentId === idParam);
            if (agentFound || isAgentMetrics) {
                setStateData('agent', agentFound, setState);
            } else {
                window.location.replace(Routes.PROF_METRICS.BASE);
            }
        }
    }, [state.agentsData]);

    const getExportPeriodsLabel = (exportProps) => {
        if (state.currentTabIndex === agentProfileTabs.INVENTORY) return '';
        if (state.currentTabIndex === agentProfileTabs.TRANSACTION_COVERAGE) {
            const selectedTimeInterval = timeIntervals && timeIntervals.find(ti => ti.intervalType === state.transactionTimePeriod);
            const startPeriod = monthAndYearFormat(selectedTimeInterval?.last?.from.replace('T', ' '));
            const endPeriod = monthAndYearFormat(selectedTimeInterval?.last?.to.replace('T', ' '));
            return `${startPeriod} - ${endPeriod}`;
        }
        if (state.currentTabIndex === agentProfileTabs.PRODUCTION) {
            const endPeriod = monthAndYearFormat(exportProps?.data?.[0]?.month);
            const startPeriod = monthAndYearFormat(exportProps?.data?.[exportProps?.data?.length - 1]?.month);
            return `${startPeriod} - ${endPeriod}`;
        }
        return '';
    };

    const exportDetails = {
        exportType: exportTypes.agentDetailSearchResults,
        agentName: state.agent?.agentName,
        selectedTabLabel: agentProfileTabNamesByIndex[state.currentTabIndex],
        mlsName: state.mlsName,
        periods: getExportPeriodsLabel(state.exportProps),
        agentData: state.agent,
    };

    useEffect(() => {
        const { productionTimePeriod, transactionTimePeriod, currentTabIndex } = state;
        const timePeriodMap = { 0: productionTimePeriod, 2: transactionTimePeriod };
        setAgentProfileExportDetails(exportDetails);
        timePeriodMap?.[currentTabIndex] && setAgentProfileTimePeriod(timePeriodMap[currentTabIndex]);
    }, [state.exportProps, state.productionTimePeriod, state.transactionTimePeriod, state.currentTabIndex, state.mlsName]);

    const transactionSearchCriteria = useMemo(
        () => isAgentMetrics ? getAmSearchCriteria(state.mlsId) : state.searchCriteria,
        [state.mlsId, state.searchCriteria]
    );

    if (!shouldRender) return (<></>);

    const profileProps = {
        agentId: state.agentId,
        mlsId: state.mlsId,
        timePeriod: state.timePeriod,
        module: module
    };

    const agentProductionListingRoute = `${isAgentMetrics ? Routes.AGENT_PRODUCTION.BASE : Routes.PROF_METRICS.BASE}${Routes.PROF_METRICS.PROD_LISTING}`;
    const handleClickChartGraph = (period) => history.push(
        `${agentProductionListingRoute}?id=${state.agentId}`
        + `&period=${period}&graph=${state.productionGraphMode}`
        + `&chartMode=${state.productionMode}`
        + `&isShowBarLabels=${state.productionIsLabelEnabled}`
        + `&intervalType=${state.productionTimePeriod.replaceAll(' ', '')}`
    );

    const getIsActive = (index) => state.currentTabIndex === index;

    const transactionCoverageProps = {
        ...profileProps,
        tableChartMode: state.transactionCoverageMode,
        searchCriteria: transactionSearchCriteria,
        timePeriod: state.transactionTimePeriod,
        agent: state.agent,
        setIsTimePeriodSelectorDisabled: (b) => setStateData('isTimePeriodSelectorDisabled', b, setState),
        sendToParent: processFromTransactionCoverage,
        isActive: getIsActive(tabIndex.TRANSACTION_COVERAGE),
        currentTabIndex: state.currentTabIndex,
        mappedAreas: mlsProps?.mappedAreas,
        setShouldStickyFooterHide,
        setState,
        transactionCoverageMode: state.transactionCoverageMode,
        mlsName: state.mlsName,
    };

    const officeHistoryProps = {
        ...profileProps,
        agent: state.agent,
        setIsTimePeriodSelectorDisabled: (b) => setStateData('isTimePeriodSelectorDisabled', b, setState),
        sendToParent: processFromOfficeHistory,
        isActive: getIsActive(tabIndex.OFFICE_HISTORY),
        currentTabIndex: state.currentTabIndex,
        setShouldStickyFooterHide
    };

    let timePeriodOptions, timePeriodToSet, currentTabPeriod;
    if (state.currentTabIndex === tabIndex.PRODUCTION) {
        timePeriodOptions = productionOptions;
        timePeriodToSet = 'productionTimePeriod';
        currentTabPeriod = state.productionTimePeriod;
        setTimePeriodData(state.productionTimePeriod);
    }
    if (state.currentTabIndex === tabIndex.TRANSACTION_COVERAGE) {
        timePeriodOptions = transactionCoverageOptions;
        timePeriodToSet = 'transactionTimePeriod';
        currentTabPeriod = state.transactionTimePeriod;
        setTimePeriodData(state.transactionTimePeriod);
    }

    const productionProps = {
        ...profileProps,
        agentData: state.agent,
        mlsId: state.mlsId,
        agentId: state.agentId,
        handleClickChartGraph,
        tableChartMode: state.productionMode,
        state,
        setState: setState,
        isXamInternalUser,
        timePeriod: state.productionTimePeriod,
        sendToParent: processFromProduction,
        isActive: getIsActive(tabIndex.PRODUCTION),
        productionGraphMode: state.productionGraphMode,
        productionIsLabelEnabled: state.productionIsLabelEnabled,
        setProductionLabels: (mode) =>
            setState((prevState) => ({
                ...prevState,
                productionIsLabelEnabled: mode
            })),
        setProductionGraphView: (mode) =>
            setState((prevState) => ({
                ...prevState,
                productionGraphMode: mode
            })),
        currentTabIndex: state.currentTabIndex,
        setShouldStickyFooterHide,
        timePeriodSelector: {
            timePeriodOptions,
            currentValue: currentTabPeriod,
            setTimePeriod: (t) => {
                setTimePeriodData(t);
                setStateData(timePeriodToSet, t, setState);
            },
            isDisabled:
                state.isTimePeriodSelectorDisabled && state.currentTabIndex !== tabIndex.PRODUCTION
        }
    };

    const setCurrentTabIndex = (currentTabIndex) => {
        const isShowTimePeriodSelector = [
            tabIndex.PRODUCTION,
            tabIndex.TRANSACTION_COVERAGE
        ].includes(currentTabIndex);

        setState((prevState) => ({
            ...prevState,
            isShowTimePeriodSelector,
            currentTabIndex
        }));
        setAgentProfileTabIndex(currentTabIndex);
        setAgentDetailNavigation({tabIndex: currentTabIndex});
    };

    const exportButton = getExportButton({
        gridRef: state.exportProps.gridRef,
        type: exportButtonTypes.primary,
        shouldUsePrimaryColumns: true,
        filename: state.currentTabIndex === agentProfileTabs.PRODUCTION && state.exportProps.filename,
        exportDetails,
    });

    const hasExportData = !!state.exportProps?.data?.length;
    const shouldShowAreaSelector = state.currentTabIndex === tabIndex.TRANSACTION_COVERAGE && hasExportData;

    const columnProps = [
        {
            children: (
                <div className="footer-container-wrapper d-flex justify-content-end align-items-center">
                    {hasExportData && exportButton}
                </div>
            ),
            sm: 12,
            align: 'right'
        }
    ];
    const chartWrapperClass = state.productionMode === tableChartModes.CHART ? 'chart-wrapper-margin' : '';
    const buildTabs = () => {
        const tabs = [
            {
                title: agentProductionTerms.production,
                content: {
                    jsx: (
                        <ChartTabWrapper>
                            <Production {...productionProps} />
                        </ChartTabWrapper>
                    )
                }
            },
            {
                title: agentProductionTerms.inventory,
                content: {
                    jsx: (
                        <ChartTabWrapper>
                            <Inventory
                                mlsId={state.mlsId}
                                agentId={state.agentId}
                                mlsName={state.mlsName}
                                isActive={getIsActive(tabIndex.INVENTORY)}
                                sendToParent={processFromInventory}
                                setShouldStickyFooterHide={setShouldStickyFooterHide}
                            />
                        </ChartTabWrapper>
                    )
                }
            },
            {
                title: agentProductionTerms.transactionCoverage,
                content: {
                    jsx: (
                        <ChartTabWrapper>
                            <TransactionCoverageTab {...transactionCoverageProps}/>
                        </ChartTabWrapper>
                    )
                }
            },
        ];

        if (!isAgentMetrics) {
            tabs.push({
                title: agentProductionTerms.officeHistory,
                content: {
                    jsx: (
                        <>
                            {state.agent && <div>
                                <OfficeHistoryTab
                                    {...officeHistoryProps}
                                />
                            </div>}
                        </>
                    )
                }
            });
        }

        return tabs;
    };

    const shouldDisplayTimePeriodSelector = false
    return (
        <>
            {isXamInternalUser && <XamAgentSelector {...xamAgentSelectorProps} />}
            <Row className={'mx-0 my-2'}>
                {state.agent && <AgentContactInfo
                        index={state.currentTabIndex}
                        agent={state.agent}
                        setCurrentTabIndex={setCurrentTabIndex}
                        handleClickChartGraph={handleClickChartGraph}
                        module={module}
                />}

                <div className={'ml-auto d-flex w-auto align-self-center'}>
                    {shouldDisplayTimePeriodSelector && state.isShowTimePeriodSelector && (
                        <TimePeriodSelector
                            options={timePeriodOptions}
                            currentValue={currentTabPeriod}
                            setTimePeriod={(t) => {
                                setTimePeriodData(t.target.value);
                                setStateData(timePeriodToSet, t.target.value, setState);
                            }}
                            isDisabled={state.isTimePeriodSelectorDisabled 
                            && state.currentTabIndex !== tabIndex.PRODUCTION}
                        />
                    )}
                </div>
            </Row>
            <div className={`agent-detail-tabs-wrapper mt-2 pb-5 ${chartWrapperClass}`}>
                <Tab
                    id='agent-detail-tabs'
                    tabTextTransform='none'
                    navSize={2}
                    contentSize={2}
                    className='secondary mb-3'
                    items={buildTabs()}
                    tabs
                    currentIndex={state.currentTabIndex}
                    setCurrentIndex={setCurrentTabIndex}
                />
            </div>
            {!state.shouldStickyFooterHide && <StickyFooter columnProps={columnProps} />}
            <DownloadResultsModal shouldShowAreaSelector={shouldShowAreaSelector} />
        </>
    );
};

AgentProfile.propTypes = {
    agentData: PropTypes.arrayOf(PropTypes.shape({
        agentId: PropTypes.string.isRequired,
    })),
    module: PropTypes.string.isRequired,
    mlsProps: PropTypes.shape({
        mappedAreas: PropTypes.object
    }),
    tokenInfo: PropTypes.shape({
        email: PropTypes.string.isRequired,
    })
};

export default AgentProfile;
